import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { PaletteMode } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import {
  COLOR_MODE_STORAGE_KEY,
  colorModeCookieName,
  readColorModeCookieValues,
} from './colorModeCookie';

const STORAGE_KEY = COLOR_MODE_STORAGE_KEY;

/** Meta tag written in `_document` with the SSR color mode — used on client hydration. */
export const HYDRATION_COLOR_MODE_META = 'elevator-initial-color-mode';

interface ColorModeContextType {
  mode: PaletteMode;
  toggleMode: () => void;
  setMode: (mode: PaletteMode) => void;
}

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

const isColorMode = (value: string): value is PaletteMode =>
  value === 'light' || value === 'dark';

/** localhost keeps a single host cookie. A real domain is shared across site and admin. */
const isSharedCookieDomain = (domain?: string): boolean =>
  Boolean(domain && domain !== 'localhost');

/** Expires the host-only cookie. Does not touch a Domain= cookie. */
const clearHostOnlyColorModeCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; max-age=0`;
};

const readLocalStorageMode = (): PaletteMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage may be disabled or throw (e.g., private browsing)
  }

  return null;
};

const readCookieMode = (): PaletteMode | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cookieDomain = process.env.COOKIE_DOMAIN;
    const name = colorModeCookieName(cookieDomain);
    let values = readColorModeCookieValues(document.cookie, name);
    // A host-only cookie and the shared Domain cookie can both be present.
    // Drop the host-only one, then the remaining value is this deploy's cookie.
    if (values.length > 1 && isSharedCookieDomain(cookieDomain)) {
      clearHostOnlyColorModeCookie(name);
      values = readColorModeCookieValues(document.cookie, name);
    }
    if (values.length === 1) {
      return values[0];
    }
    if (values.length > 1) {
      const stored = readLocalStorageMode();
      if (stored && values.includes(stored)) {
        return stored;
      }
      return values[values.length - 1];
    }
    return null;
  } catch {
    // Cookies may be disabled or inaccessible
    return null;
  }
};

/** This deploy's cookie wins over localStorage. A parent-domain cookie is a different name. */
const readStoredMode = (): PaletteMode | null =>
  readCookieMode() ?? readLocalStorageMode();

const writeStoredMode = (mode: PaletteMode): void => {
  localStorage.setItem(STORAGE_KEY, mode);
  const cookieDomain = process.env.COOKIE_DOMAIN;
  const name = colorModeCookieName(cookieDomain);
  if (isSharedCookieDomain(cookieDomain)) {
    clearHostOnlyColorModeCookie(name);
  }
  const domainAttr = cookieDomain ? `; domain=${cookieDomain}` : '';
  document.cookie = `${name}=${mode}; path=/; max-age=31536000${domainAttr}`;
  document.documentElement.setAttribute('data-color-mode', mode);
};

/**
 * MUI color-scheme storage that reads the same cookie as the blocking script.
 * Other keys (scheme name storage) keep their defaults.
 */
export const colorModeStorageManager = ({ key }: { key: string }) => ({
  get: (defaultValue: string) => {
    if (key !== STORAGE_KEY) return defaultValue;
    return readStoredMode() ?? defaultValue;
  },
  set: (value: string) => {
    if (key !== STORAGE_KEY || !isColorMode(value) || typeof document === 'undefined')
      return;
    try {
      writeStoredMode(value);
    } catch {
      // Cookies or localStorage may be unavailable.
    }
  },
  subscribe: () => () => {},
});

/**
 * Gets the initial color mode by checking the data attribute set by blocking script,
 * then falling back to localStorage and system preference.
 * The blocking script in _document.tsx sets data-color-mode before React hydrates.
 */
const getInitialMode = (): PaletteMode => {
  if (typeof window === 'undefined') {
    return 'light'; // SSR fallback
  }

  const storedMode = readStoredMode();
  if (storedMode) {
    return storedMode;
  }

  // Fallback to data attribute set by blocking script
  try {
    const dataMode = document.documentElement.getAttribute('data-color-mode');
    if (dataMode === 'light' || dataMode === 'dark') {
      return dataMode as PaletteMode;
    }
  } catch (error) {
    // Fall through to system preference
  }

  // Check system preference synchronously
  try {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  } catch (error) {
    // Media query may not be supported, default to light
    return 'light';
  }
};

/**
 * Color mode for the first client render — must match `_document` SSR / Emotion extraction.
 * On full page load, `initialColorMode` is not passed to `_app`; read the SSR meta tag instead.
 */
export const resolveHydrationColorMode = (ssrMode?: PaletteMode): PaletteMode => {
  if (ssrMode === 'light' || ssrMode === 'dark') {
    return ssrMode;
  }

  if (typeof document !== 'undefined') {
    const meta = document.querySelector(`meta[name="${HYDRATION_COLOR_MODE_META}"]`);
    const content = meta?.getAttribute('content');
    if (content === 'light' || content === 'dark') {
      return content;
    }
    return getInitialMode();
  }

  return 'light';
};

/**
 * Provider component for color mode context
 * Must wrap your app to enable dark mode functionality
 */
export const ColorModeProvider: React.FC<{
  children: React.ReactNode;
  initialMode?: PaletteMode;
}> = ({ children, initialMode }) => {
  // Initialize state - use initialMode from SSR (cookie) if provided, otherwise read from client
  // This MUST match what SSR rendered to prevent hydration mismatch
  const [mode, setModeState] = useState<PaletteMode>(() => {
    // If initialMode is provided (from SSR cookie), ALWAYS use it - don't override
    // This ensures SSR and client render with the same theme
    if (initialMode === 'light' || initialMode === 'dark') {
      return initialMode;
    }
    // Only read from client if initialMode wasn't provided (client-side navigation)
    return getInitialMode();
  });
  // Static pages bake initialMode as light. Wait to persist until the saved cookie is applied,
  // otherwise the first effect writes light back over a dark preference.
  const [canPersist, setCanPersist] = useState(false);

  // Sync once when SSR-provided initialMode first becomes available / changes.
  // Prefer the persisted cookie when present so a client getInitialProps default
  // of "light" (no req.headers.cookie) cannot clobber dark mode after HMR.
  const [prevInitialMode, setPrevInitialMode] = useState(initialMode);
  if (initialMode !== prevInitialMode) {
    setPrevInitialMode(initialMode);
    if (initialMode === 'light' || initialMode === 'dark') {
      setModeState(readStoredMode() ?? initialMode);
    }
  }

  // Static HTML cannot see the request cookie. Apply the saved mode after hydration.
  // Deferred so the first paint matches the server render, and so this does not
  // write the baked light mode over a dark cookie.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const stored = readStoredMode();
      if (stored) {
        setModeState(stored);
      }
      setCanPersist(true);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  // Persist to both cookie and localStorage whenever mode changes
  useEffect(() => {
    if (typeof window === 'undefined' || !canPersist) {
      return;
    }

    try {
      // Cookie and localStorage. Domain from COOKIE_DOMAIN shares the preference
      // across site and admin. Host-only if unset.
      writeStoredMode(mode);
    } catch (error) {
      // localStorage/cookies may be disabled or quota exceeded
      // Silently fail - user preference won't persist but app continues to work
    }
  }, [mode, canPersist]);

  const toggleMode = useCallback(() => {
    // Save scroll position before mode change to prevent scroll reset
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));

    // Restore scroll position after theme update completes
    // Use double requestAnimationFrame to ensure DOM has fully updated
    if (typeof window !== 'undefined' && scrollY > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      });
    }
  }, []);

  const setColorMode = useCallback((newMode: PaletteMode) => {
    setModeState(newMode);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleMode,
      setMode: setColorMode,
    }),
    [mode, toggleMode, setColorMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

/**
 * Hook for accessing color mode state
 *
 * Must be used within a ColorModeProvider
 *
 * @example
 * ```tsx
 * const { mode, toggleMode } = useColorMode();
 *
 * const theme = useMemo(
 *   () => createTheme({ palette: { mode } }),
 *   [mode]
 * );
 * ```
 */
export const useColorMode = (): ColorModeContextType => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
};
